import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Phone, Activity, UserCog, Users2, Sparkles } from "lucide-react";

const SUB_ROLES = [
  { v: "caller", label: "Caller" },
  { v: "agent", label: "Agent" },
];

export default function AdminTeamTab() {
  const [members, setMembers] = useState([]);
  const [perf, setPerf] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", sub_role: "caller", status: "active" });
  const [editing, setEditing] = useState(null); // id or null

  const load = async () => {
    setBusy(true);
    try {
      const [m, p] = await Promise.all([
        api.get("/admin/team-members"),
        api.get("/admin/team-performance"),
      ]);
      setMembers(m.data || []);
      setPerf(p.data || []);
    } catch (e) {
      toast.error("Failed to load team");
    }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm({ name: "", mobile: "", sub_role: "caller", status: "active" }); setEditing(null); };

  const save = async () => {
    const digits = form.mobile.replace(/\D/g, "").slice(-10);
    if (!form.name.trim()) return toast.error("Name required");
    if (digits.length !== 10) return toast.error("Mobile must be 10 digits");
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/team-members/${editing}`, { ...form, mobile: digits });
        toast.success("Team member updated");
      } else {
        await api.post("/admin/team-members", { ...form, mobile: digits });
        toast.success("Team member added");
      }
      reset();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const toggleStatus = async (m) => {
    try {
      await api.patch(`/admin/team-members/${m.id}`, { status: m.status === "active" ? "inactive" : "active" });
      toast.success(`${m.name} is now ${m.status === "active" ? "inactive" : "active"}`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.name}? Their assigned leads will be unassigned.`)) return;
    try {
      await api.delete(`/admin/team-members/${m.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const startEdit = (m) => {
    setEditing(m.id);
    setForm({ name: m.name || "", mobile: m.mobile || "", sub_role: m.sub_role, status: m.status });
  };

  const autoAssign = async () => {
    try {
      const r = await api.post("/admin/leads/auto-assign");
      toast.success(`Assigned ${r.data.assigned} leads across ${r.data.members} members`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-team-tab">
      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="team-form-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading font-semibold text-lg tracking-tight inline-flex items-center gap-2">
              <UserCog size={16} /> {editing ? "Edit team member" : "Add team member"}
            </h3>
            <p className="text-[11px] text-[#71717A]">Team members log in via mobile + OTP.</p>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={autoAssign} data-testid="team-auto-assign-btn">
            <Sparkles size={13} className="mr-1.5" /> Auto-assign unassigned leads
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <Lbl>Name</Lbl>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ravi Kumar" className="rounded-sm" data-testid="team-form-name" />
          </div>
          <div>
            <Lbl>Mobile (10-digit)</Lbl>
            <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="9876543210" className="rounded-sm font-mono" inputMode="numeric" data-testid="team-form-mobile" />
          </div>
          <div>
            <Lbl>Role</Lbl>
            <select value={form.sub_role} onChange={(e) => setForm({ ...form, sub_role: e.target.value })} className="w-full h-9 rounded-sm border border-[#E4E4E7] px-2 text-[13px] bg-white" data-testid="team-form-sub-role">
              {SUB_ROLES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <Lbl>Status</Lbl>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full h-9 rounded-sm border border-[#E4E4E7] px-2 text-[13px] bg-white" data-testid="team-form-status">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button className="btn-brand rounded-sm" onClick={save} disabled={busy} data-testid="team-form-save-btn">
            <Plus size={13} className="mr-1.5" /> {editing ? "Save changes" : "Add team member"}
          </Button>
          {editing && (
            <Button variant="outline" className="rounded-sm" onClick={reset} data-testid="team-form-cancel">Cancel</Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="team-members-list">
        <div className="px-4 py-3 border-b border-[#E4E4E7] bg-[#FAFAFA] flex items-center justify-between">
          <h3 className="font-heading font-semibold text-[14px] tracking-tight inline-flex items-center gap-2">
            <Users2 size={14} /> Team members ({members.length})
          </h3>
        </div>
        <table className="w-full text-[13px]">
          <thead className="bg-[#FAFAFA] border-b border-[#E4E4E7]">
            <tr>
              <Th>Name</Th>
              <Th>Mobile</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Total</Th>
              <Th>Visits</Th>
              <Th>Closed</Th>
              <Th>Conv %</Th>
              <Th className="text-right pr-4">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-[#A1A1AA]">No team members yet — add your first one above.</td></tr>
            )}
            {members.map((m) => {
              const p = perf.find((x) => x.id === m.id) || {};
              return (
                <tr key={m.id} className="border-b border-[#F4F4F5] hover:bg-[#FAFAFA]" data-testid={`team-member-row-${m.id}`}>
                  <Td className="font-semibold text-[#09090B]">{m.name}</Td>
                  <Td><span className="font-mono text-[12px]">+91 {m.mobile}</span></Td>
                  <Td><span className="capitalize">{m.sub_role}</span></Td>
                  <Td>
                    <button onClick={() => toggleStatus(m)} className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${m.status === "active" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`} data-testid={`team-member-toggle-${m.id}`}>
                      {m.status}
                    </button>
                  </Td>
                  <Td className="font-mono-data">{p.total_leads ?? 0}</Td>
                  <Td className="font-mono-data">{p.site_visits ?? 0}</Td>
                  <Td className="font-mono-data text-[#0055FF]">{p.closed ?? 0}</Td>
                  <Td className="font-mono-data font-semibold">{(p.conversion_rate ?? 0).toFixed(1)}%</Td>
                  <Td className="text-right pr-4 whitespace-nowrap">
                    <Button size="sm" variant="outline" className="rounded-sm h-7 px-2 mr-1" onClick={() => startEdit(m)} data-testid={`team-member-edit-${m.id}`}>
                      <Edit3 size={11} />
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-sm h-7 px-2 text-[#DC2626] hover:bg-[#FEE2E2]" onClick={() => remove(m)} data-testid={`team-member-delete-${m.id}`}>
                      <Trash2 size={11} />
                    </Button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Lbl = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1">{children}</div>
);
const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-[#52525B] ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>
);
